import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import AgendaBuilder from "../components/agenda/AgendaBuilder";
import {
  ArrowLeft,
  Upload,
  FileText,
  Mic,
  Paperclip,
  Trash2,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  FileDown,
  Wand2,
  Plus,
  Calendar,
  MapPin,
  User,
  MoreHorizontal,
  ChevronRight,
  Play,
  CheckCircle2,
  Circle,
  Send,
  Edit,
  Download,
  FileSpreadsheet,
  Shield,
  Gavel,
  HandMetal,
  Vote,
  Link2,
  Eye,
  Building2,
  Home,
  Archive,
  UserCheck,
  UserX,
  UserPlus as UserInvite,
  ListOrdered,
  Settings,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700 border-slate-300" },
  in_review: { label: "In Review", color: "bg-amber-100 text-amber-700 border-amber-300" },
  approved: { label: "Approved", color: "bg-blue-100 text-blue-700 border-blue-300" },
  signed: { label: "Signed", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  locked: { label: "Locked", color: "bg-red-100 text-red-700 border-red-300" },
};

export default function MeetingDetailPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Core state
  const [meeting, setMeeting] = useState(null);
  const [minutes, setMinutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState(null);
  
  // Processing state
  const [generating, setGenerating] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  
  // Undo delete state
  const [deletedUpload, setDeletedUpload] = useState(null);
  const [undoTimeout, setUndoTimeout] = useState(null);
  
  // Modal state
  const [showAttendeeModal, setShowAttendeeModal] = useState(false);
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  
  // Bulk import state
  const [csvText, setCsvText] = useState("");
  const [parsedAttendees, setParsedAttendees] = useState([]);
  const [importingAttendees, setImportingAttendees] = useState(false);
  
  // Attendees state (from backend)
  const [attendees, setAttendees] = useState({ present: [], apology: [], invitee: [] });
  
  // Form state
  const [newAttendee, setNewAttendee] = useState({ name: "", email: "", role: "", status: "present", director_type: "" });
  const [newResolution, setNewResolution] = useState({
    title: "", text: "", moved_by: "", seconded_by: "",
    vote_method: "voice", outcome: "approved",
    votes_for: null, votes_against: null, abstentions: null,
  });
  const [newDeclaration, setNewDeclaration] = useState({
    person_name: "", role_title: "", declaration_type: "interest",
    description: "", action_taken: "",
  });
  
  // Governance state
  const [resolutions, setResolutions] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  
  // Quorum validation state
  const [quorumValidation, setQuorumValidation] = useState(null);
  const [validatingQuorum, setValidatingQuorum] = useState(false);
  const [showQuorumRulesModal, setShowQuorumRulesModal] = useState(false);
  const [quorumRule, setQuorumRule] = useState({
    min_total_attendees: null,
    type_requirements: [],
  });
  const [savingQuorumRule, setSavingQuorumRule] = useState(false);

  // Fetch data on mount and poll every 5 seconds
  useEffect(() => {
    fetchMeeting();
    fetchBranding();
    fetchGovernanceData();
    fetchAttendees();
    const meetingInterval = setInterval(fetchMeeting, 5000);
    return () => {
      clearInterval(meetingInterval);
      if (undoTimeout) clearTimeout(undoTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  // Poll processing status more frequently when transcribing
  useEffect(() => {
    if (transcribing || processingStatus?.is_processing) {
      const statusInterval = setInterval(fetchProcessingStatus, 3000);
      return () => clearInterval(statusInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcribing, processingStatus?.is_processing, meetingId]);

  const fetchProcessingStatus = async () => {
    try {
      const res = await axios.get(`${API}/transcription/meetings/${meetingId}/status`);
      setProcessingStatus(res.data);
      
      // Auto-update transcribing state based on job status
      if (res.data.transcription_job) {
        const jobStatus = res.data.transcription_job.status;
        if (jobStatus === "completed") {
          setTranscribing(false);
          toast.success("Transcription completed!");
          fetchMeeting();
        } else if (jobStatus === "failed") {
          setTranscribing(false);
          toast.error(res.data.transcription_job.error || "Transcription failed");
        } else if (jobStatus === "running" || jobStatus === "queued") {
          setTranscribing(true);
        }
      }
    } catch (error) {
      // Silently fail status polling
    }
  };

  const fetchMeeting = async () => {
    try {
      const [meetingRes, minutesRes] = await Promise.all([
        axios.get(`${API}/meetings/${meetingId}`),
        axios.get(`${API}/meetings/${meetingId}/minutes`).catch(() => null),
      ]);
      setMeeting(meetingRes.data);
      if (minutesRes?.data) setMinutes(minutesRes.data);
    } catch (error) {
      toast.error("Failed to load meeting");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async () => {
    try {
      const res = await axios.get(`${API}/meetings/${meetingId}/attendees`);
      setAttendees({
        present: res.data.present || [],
        apology: res.data.apology || [],
        invitee: res.data.invitee || [],
      });
    } catch (error) {
      // Fallback to meeting.attendees if endpoint fails
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/company/branding`);
      setBranding(response.data);
    } catch (error) {
      // Use defaults
    }
  };

  const fetchGovernanceData = async () => {
    try {
      const [resRes, decRes] = await Promise.all([
        axios.get(`${API}/meetings/${meetingId}/resolutions`).catch(() => ({ data: { resolutions: [] } })),
        axios.get(`${API}/meetings/${meetingId}/declarations`).catch(() => ({ data: { declarations: [] } })),
      ]);
      setResolutions(resRes.data.resolutions || []);
      setDeclarations(decRes.data.declarations || []);
    } catch (error) {
      console.error("Failed to fetch governance data");
    }
  };

  // Quorum validation handlers
  const handleValidateQuorum = async () => {
    setValidatingQuorum(true);
    try {
      const response = await axios.post(`${API}/meetings/${meetingId}/validate-quorum`);
      setQuorumValidation(response.data);
      if (response.data.quorum_met) {
        toast.success("Quorum requirements met!");
      } else {
        toast.warning("Quorum requirements not met");
      }
    } catch (error) {
      toast.error("Failed to validate quorum");
    } finally {
      setValidatingQuorum(false);
    }
  };

  const handleSaveQuorumRule = async () => {
    setSavingQuorumRule(true);
    try {
      // Filter out empty requirements
      const filteredRequirements = quorumRule.type_requirements.filter(
        req => req.director_type && req.min_count > 0
      );
      
      const payload = {
        min_total_attendees: quorumRule.min_total_attendees || null,
        type_requirements: filteredRequirements,
        committee_specific: false,
      };
      
      await axios.put(`${API}/meetings/${meetingId}/quorum-rule`, payload);
      toast.success("Quorum rule saved");
      setShowQuorumRulesModal(false);
      fetchMeeting();
    } catch (error) {
      toast.error("Failed to save quorum rule");
    } finally {
      setSavingQuorumRule(false);
    }
  };

  const addQuorumTypeRequirement = () => {
    setQuorumRule({
      ...quorumRule,
      type_requirements: [
        ...quorumRule.type_requirements,
        { director_type: "", min_count: 1 }
      ]
    });
  };

  const removeQuorumTypeRequirement = (index) => {
    setQuorumRule({
      ...quorumRule,
      type_requirements: quorumRule.type_requirements.filter((_, i) => i !== index)
    });
  };

  const updateQuorumTypeRequirement = (index, field, value) => {
    const updated = [...quorumRule.type_requirements];
    updated[index] = { ...updated[index], [field]: value };
    setQuorumRule({ ...quorumRule, type_requirements: updated });
  };

  // File upload handlers
  const onAudioDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", "audio");
    
    try {
      toast.info("Uploading audio...");
      await axios.post(`${API}/meetings/${meetingId}/upload`, formData);
      toast.success("Audio uploaded successfully");
      fetchMeeting();
    } catch (error) {
      toast.error("Failed to upload audio");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const onDocDrop = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_type", "document");
      
      try {
        await axios.post(`${API}/meetings/${meetingId}/upload`, formData);
        toast.success(`${file.name} uploaded`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    fetchMeeting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const audioDropzone = useDropzone({
    onDrop: onAudioDrop,
    accept: { "audio/*": [".mp3", ".wav", ".m4a", ".ogg", ".webm"] },
    maxFiles: 1,
  });

  const docDropzone = useDropzone({
    onDrop: onDocDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
  });

  // Action handlers
  const handleTranscribe = async () => {
    setTranscribing(true);
    try {
      // Use new transcription jobs API
      await axios.post(`${API}/transcription/meetings/${meetingId}/start`);
      toast.success("Transcription started - this may take a few minutes");
      fetchProcessingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start transcription");
      setTranscribing(false);
    }
  };

  const handleRetryTranscription = async () => {
    setTranscribing(true);
    try {
      await axios.post(`${API}/transcription/meetings/${meetingId}/retry`);
      toast.success("Transcription retry started");
      fetchProcessingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to retry transcription");
      setTranscribing(false);
    }
  };

  const handleGenerateMinutes = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/meetings/${meetingId}/generate-minutes`);
      toast.success("Minutes generated successfully");
      fetchMeeting();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate minutes");
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/meetings/${meetingId}/export/${format}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${meeting?.name || "Minutes"}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const handleAddAttendee = async () => {
    if (!newAttendee.name.trim()) return;
    try {
      // Use new attendees API with proper type values including director_type for quorum
      const attendeeData = {
        person_name: newAttendee.name,
        role_title: newAttendee.role || "",
        attendance_type: newAttendee.status || "present",  // present/apology/invitee
        director_type: newAttendee.director_type || null,  // For quorum validation
      };
      
      const response = await axios.post(`${API}/meetings/${meetingId}/attendees`, attendeeData);
      
      // Append the returned attendee to the correct list
      const addedAttendee = response.data;
      setAttendees(prev => ({
        ...prev,
        [addedAttendee.attendance_type]: [...(prev[addedAttendee.attendance_type] || []), addedAttendee]
      }));
      
      toast.success("Attendee added");
      setShowAttendeeModal(false);
      setNewAttendee({ name: "", email: "", role: "", status: "present", director_type: "" });
      
      // Also refetch to ensure sync
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add attendee");
    }
  };

  const handleRemoveAttendee = async (attendeeId) => {
    if (!window.confirm("Remove this attendee?")) return;
    try {
      await axios.delete(`${API}/meetings/${meetingId}/attendees/${attendeeId}`);
      toast.success("Attendee removed");
      fetchAttendees();
    } catch (error) {
      toast.error("Failed to remove attendee");
    }
  };

  // CSV parsing for bulk import
  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) return [];
    
    const headers = lines[0].toLowerCase().split(",").map(h => h.trim());
    const attendees = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map(v => v.trim());
      if (values.length < 1 || !values[0]) continue;
      
      const attendee = {
        name: values[headers.indexOf("name")] || values[0] || "",
        email: values[headers.indexOf("email")] || values[1] || "",
        role: values[headers.indexOf("role")] || values[headers.indexOf("title")] || values[2] || "",
        status: (values[headers.indexOf("status")] || "present").toLowerCase(),
      };
      
      if (attendee.name) {
        attendees.push(attendee);
      }
    }
    
    return attendees;
  };

  const handleCSVChange = (text) => {
    setCsvText(text);
    const parsed = parseCSV(text);
    setParsedAttendees(parsed);
  };

  const handleCSVFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      handleCSVChange(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    if (parsedAttendees.length === 0) {
      toast.error("No valid attendees to import");
      return;
    }
    
    setImportingAttendees(true);
    try {
      // Use new attendees API for each attendee
      for (const attendee of parsedAttendees) {
        await axios.post(`${API}/meetings/${meetingId}/attendees`, {
          person_name: attendee.name,
          role_title: attendee.role || "",
          attendance_type: attendee.status || "present"
        });
      }
      toast.success(`Imported ${parsedAttendees.length} attendees`);
      setShowBulkImportModal(false);
      setCsvText("");
      setParsedAttendees([]);
      fetchAttendees();
    } catch (error) {
      toast.error("Failed to import attendees");
    } finally {
      setImportingAttendees(false);
    }
  };

  const handleDeleteUpload = async (uploadId, filename) => {
    try {
      const response = await axios.delete(`${API}/meetings/${meetingId}/upload/${uploadId}`);
      
      // Store deleted upload info for undo
      setDeletedUpload({ uploadId, filename });
      
      // Show toast with undo button
      toast.success(
        <div className="flex items-center justify-between gap-4">
          <span>File deleted</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleUndoDelete(uploadId)}
            className="text-xs"
          >
            Undo (30s)
          </Button>
        </div>,
        { duration: 30000 }
      );
      
      // Clear undo option after 30 seconds
      if (undoTimeout) clearTimeout(undoTimeout);
      const timeout = setTimeout(() => {
        setDeletedUpload(null);
      }, 30000);
      setUndoTimeout(timeout);
      
      // Refetch meeting but DO NOT clear state on error
      fetchMeeting();
    } catch (error) {
      // On error, DO NOT clear meeting state - just show error
      toast.error(error.response?.data?.detail || "Failed to delete file");
      // Refetch to ensure state is accurate
      fetchMeeting();
    }
  };

  const handleUndoDelete = async (uploadId) => {
    try {
      await axios.post(`${API}/meetings/${meetingId}/upload/${uploadId}/restore`);
      toast.success("File restored");
      setDeletedUpload(null);
      if (undoTimeout) {
        clearTimeout(undoTimeout);
        setUndoTimeout(null);
      }
      fetchMeeting();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore file");
    }
  };

  const handleAddResolution = async () => {
    if (!newResolution.title.trim()) return;
    try {
      await axios.post(`${API}/meetings/${meetingId}/resolutions`, newResolution);
      toast.success("Resolution added");
      setShowResolutionModal(false);
      setNewResolution({
        title: "", text: "", moved_by: "", seconded_by: "",
        vote_method: "voice", outcome: "approved",
        votes_for: null, votes_against: null, abstentions: null,
      });
      fetchGovernanceData();
    } catch (error) {
      toast.error("Failed to add resolution");
    }
  };

  const handleAddDeclaration = async () => {
    if (!newDeclaration.person_name.trim()) return;
    try {
      await axios.post(`${API}/meetings/${meetingId}/declarations`, newDeclaration);
      toast.success("Declaration added");
      setShowDeclarationModal(false);
      setNewDeclaration({
        person_name: "", role_title: "", declaration_type: "interest",
        description: "", action_taken: "",
      });
      fetchGovernanceData();
    } catch (error) {
      toast.error("Failed to add declaration");
    }
  };

  // Computed values
  const uploads = meeting?.uploads || [];
  const audioUpload = uploads.find(u => u.file_type === "audio");
  const documents = uploads.filter(u => u.file_type === "document" || u.file_type === "attachment");
  const hasTranscript = meeting?.transcription_status === "completed";
  const hasMinutes = !!minutes || meeting?.minutes_generated;
  const minutesStatus = minutes?.status || "draft";
  const statusConfig = STATUS_CONFIG[minutesStatus] || STATUS_CONFIG.draft;
  
  // Use attendees state from fetchAttendees API (grouped by type)
  const presentAttendees = attendees.present || [];
  const apologyAttendees = attendees.apology || [];
  const inviteeAttendees = attendees.invitee || [];

  // Theme colors from branding
  const primaryColor = branding?.primary_color || "#0e4f55";
  const secondaryColor = branding?.secondary_color || "#14b8a6";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  if (!meeting) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top App Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo & Nav */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                {branding?.logo_url ? (
                  <img src={branding.logo_url} alt="Logo" className="h-8 w-auto" />
                ) : (
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: primaryColor }}
                  >
                    M
                  </div>
                )}
                <span className="font-semibold text-slate-900 hidden sm:block">MyMeetingsPal</span>
              </div>
              
              <nav className="hidden md:flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/")}
                  className="text-slate-600 hover:text-slate-900"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-900 bg-slate-100"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Meetings
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/")}
                  className="text-slate-600 hover:text-slate-900"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive
                </Button>
              </nav>
            </div>

            {/* Right: User */}
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {user?.name?.charAt(0) || "U"}
                    </div>
                    <span className="hidden sm:block text-slate-700">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate("/settings")}>
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/")}>
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Page Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left: Title & Breadcrumb */}
            <div className="flex items-start gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="mt-1 text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                  <span>Meetings</span>
                  <ChevronRight className="w-4 h-4" />
                  <span>{meeting.committee_name || "Board Meeting"}</span>
                </div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="meeting-title">
                  {meeting.name}
                </h1>
                {meeting.committee_name && (
                  <p className="text-slate-500 mt-1">{meeting.committee_name}</p>
                )}
              </div>
            </div>

            {/* Right: Status & Actions */}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge 
                variant="outline" 
                className={`px-3 py-1 text-sm font-medium ${statusConfig.color}`}
                data-testid="minutes-status-badge"
              >
                {statusConfig.label}
              </Badge>
              
              {hasMinutes && minutesStatus === "draft" && (
                <Button
                  onClick={() => navigate(`/meetings/${meetingId}/minutes`)}
                  className="text-white"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="submit-review-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit for Review
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={() => navigate(`/meetings/${meetingId}/minutes`)}
                disabled={!hasMinutes}
                data-testid="edit-draft-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                {hasMinutes ? "Edit Draft" : "No Minutes"}
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport("docx")} disabled={!hasMinutes}>
                    <FileText className="w-4 h-4 mr-2" />
                    Download Word
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport("pdf")} disabled={!hasMinutes}>
                    <FileDown className="w-4 h-4 mr-2" />
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowResolutionModal(true)}>
                    <Gavel className="w-4 h-4 mr-2" />
                    Add Resolution
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowDeclarationModal(true)}>
                    <HandMetal className="w-4 h-4 mr-2" />
                    Add Declaration
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* LEFT COLUMN - Primary Content (8 cols) */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Meeting Details Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="meeting-details-card">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" style={{ color: primaryColor }} />
                Meeting Details
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Date</p>
                  <p className="font-medium text-slate-900">{meeting.date || "Not set"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Time</p>
                  <p className="font-medium text-slate-900">
                    {meeting.start_time && meeting.end_time 
                      ? `${meeting.start_time} - ${meeting.end_time}`
                      : meeting.start_time || "Not set"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Venue</p>
                  <p className="font-medium text-slate-900">{meeting.venue || "Not set"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Next Meeting</p>
                  <p className="font-medium text-slate-900">{meeting.next_meeting_date || "TBD"}</p>
                </div>
              </div>
            </div>

            {/* Quorum & Attendance Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="attendance-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5" style={{ color: primaryColor }} />
                  Quorum & Attendance
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkImportModal(true)}
                    data-testid="bulk-import-btn"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Import CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAttendeeModal(true)}
                    data-testid="add-attendee-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Quorum Confirmation */}
              <div 
                className="rounded-lg p-4 mb-6"
                style={{ backgroundColor: meeting.quorum_confirmed ? `${secondaryColor}15` : "#fef3c7" }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {meeting.quorum_confirmed ? (
                      <CheckCircle className="w-6 h-6" style={{ color: secondaryColor }} />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-amber-500" />
                    )}
                    <div>
                      <p className="font-semibold text-slate-900">
                        {meeting.quorum_confirmed ? "Quorum Confirmed" : "Quorum Not Confirmed"}
                      </p>
                      <p className="text-sm text-slate-600">
                        {meeting.quorum_text || `${presentAttendees.length} members present`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Load existing rule if present
                        if (meeting.quorum_rule) {
                          setQuorumRule({
                            min_total_attendees: meeting.quorum_rule.min_total_attendees || null,
                            type_requirements: meeting.quorum_rule.type_requirements || [],
                          });
                        }
                        setShowQuorumRulesModal(true);
                      }}
                      data-testid="configure-quorum-btn"
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Rules
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleValidateQuorum}
                      disabled={validatingQuorum}
                      data-testid="validate-quorum-btn"
                    >
                      {validatingQuorum ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                      )}
                      Validate
                    </Button>
                  </div>
                </div>
                
                {/* Quorum Validation Results */}
                {quorumValidation && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className={`flex items-center gap-2 mb-2 ${quorumValidation.quorum_met ? "text-emerald-600" : "text-red-600"}`}>
                      {quorumValidation.quorum_met ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <AlertCircle className="w-5 h-5" />
                      )}
                      <span className="font-medium">
                        {quorumValidation.quorum_met ? "All quorum requirements met" : "Quorum requirements not met"}
                      </span>
                    </div>
                    
                    {/* Show detailed breakdown */}
                    <div className="text-sm text-slate-600 space-y-1">
                      <p>Present: {quorumValidation.present_count} attendees</p>
                      {quorumValidation.min_required && (
                        <p>Minimum required: {quorumValidation.min_required}</p>
                      )}
                      
                      {/* Type validations */}
                      {quorumValidation.type_validations?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="font-medium text-slate-700">Director Type Requirements:</p>
                          {quorumValidation.type_validations.map((tv, idx) => (
                            <p key={idx} className={tv.satisfied ? "text-emerald-600" : "text-red-600"}>
                              {tv.satisfied ? "✓" : "✗"} {tv.type}: {tv.present}/{tv.required} required
                            </p>
                          ))}
                        </div>
                      )}
                      
                      {/* Failure reasons */}
                      {quorumValidation.reasons?.length > 0 && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-red-700">
                          <p className="font-medium">Missing:</p>
                          <ul className="list-disc list-inside">
                            {quorumValidation.reasons.map((reason, idx) => (
                              <li key={idx}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Present */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-emerald-500" />
                  Present ({presentAttendees.length})
                </h3>
                {presentAttendees.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {presentAttendees.map((attendee, idx) => (
                      <div 
                        key={idx}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full"
                      >
                        <span className="text-sm font-medium text-slate-900">{attendee.person_name || attendee.name}</span>
                        {(attendee.role_title || attendee.role) && (
                          <span className="text-xs text-slate-500">({attendee.role_title || attendee.role})</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">None</p>
                )}
              </div>

              {/* Apologies */}
              {apologyAttendees.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <UserX className="w-4 h-4 text-amber-500" />
                    Apologies ({apologyAttendees.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {apologyAttendees.map((attendee, idx) => (
                      <div 
                        key={idx}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full"
                      >
                        <span className="text-sm font-medium text-slate-900">{attendee.person_name || attendee.name}</span>
                        {(attendee.role_title || attendee.role) && (
                          <span className="text-xs text-slate-500">({attendee.role_title || attendee.role})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invitees/Observers */}
              {inviteeAttendees.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <UserInvite className="w-4 h-4 text-blue-500" />
                    Invitees/Observers ({inviteeAttendees.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {inviteeAttendees.map((attendee, idx) => (
                      <div 
                        key={idx}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full"
                      >
                        <span className="text-sm font-medium text-slate-900">{attendee.person_name || attendee.name}</span>
                        {(attendee.role_title || attendee.role) && (
                          <span className="text-xs text-slate-500">({attendee.role_title || attendee.role})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Agenda Builder Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="agenda-card">
              <AgendaBuilder 
                meetingId={meetingId} 
                meeting={meeting} 
                onUpdate={fetchMeeting}
              />
            </div>

            {/* Governance: Resolutions & Declarations */}
            {(resolutions.length > 0 || declarations.length > 0) && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="governance-card">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5" style={{ color: primaryColor }} />
                  Governance
                </h2>

                {/* Resolutions */}
                {resolutions.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <Gavel className="w-4 h-4" />
                      Resolutions ({resolutions.length})
                    </h3>
                    <div className="space-y-3">
                      {resolutions.map((res) => (
                        <div key={res.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{res.title}</p>
                              {res.text && <p className="text-sm text-slate-600 mt-1">{res.text}</p>}
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                {res.moved_by && <span>Moved: {res.moved_by}</span>}
                                {res.seconded_by && <span>Seconded: {res.seconded_by}</span>}
                              </div>
                            </div>
                            <Badge variant={res.outcome === "approved" ? "default" : "secondary"} className={
                              res.outcome === "approved" ? "bg-emerald-100 text-emerald-700" :
                              res.outcome === "rejected" ? "bg-red-100 text-red-700" :
                              "bg-slate-100 text-slate-700"
                            }>
                              {res.outcome?.charAt(0).toUpperCase() + res.outcome?.slice(1)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Declarations */}
                {declarations.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <HandMetal className="w-4 h-4" />
                      Declarations of Interest ({declarations.length})
                    </h3>
                    <div className="space-y-3">
                      {declarations.map((dec) => (
                        <div key={dec.id} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{dec.person_name}</p>
                              <p className="text-sm text-slate-600 mt-1">{dec.description}</p>
                              {dec.action_taken && (
                                <p className="text-xs text-slate-500 mt-2">Action: {dec.action_taken}</p>
                              )}
                            </div>
                            <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                              {dec.declaration_type}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN - Secondary Content (4 cols) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Processing Status Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="processing-card">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Processing Status</h2>
              
              <div className="space-y-4">
                {/* Step 1: Audio */}
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    audioUpload ? "bg-emerald-100" : "bg-slate-100"
                  }`}>
                    {audioUpload ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${audioUpload ? "text-slate-900" : "text-slate-500"}`}>
                      Audio Uploaded
                    </p>
                    {audioUpload && (
                      <p className="text-xs text-slate-500 mt-0.5">{audioUpload.filename}</p>
                    )}
                  </div>
                </div>

                {/* Connector */}
                <div className="ml-4 w-px h-4 bg-slate-200" />

                {/* Step 2: Transcription */}
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    hasTranscript ? "bg-emerald-100" :
                    meeting?.transcription_status === "processing" ? "bg-amber-100" : "bg-slate-100"
                  }`}>
                    {hasTranscript ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : meeting?.transcription_status === "processing" ? (
                      <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${hasTranscript ? "text-slate-900" : "text-slate-500"}`}>
                      Transcription
                    </p>
                    
                    {/* Show progress bar when transcribing */}
                    {(transcribing || processingStatus?.is_processing) && processingStatus?.transcription_job?.progress ? (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-blue-600 font-medium">
                            {processingStatus.transcription_job.progress.message || "Processing..."}
                          </span>
                          <span className="text-slate-500">
                            {processingStatus.transcription_job.progress.percent || 0}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-blue-500 h-full rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${processingStatus.transcription_job.progress.percent || 0}%` }}
                          />
                        </div>
                        {processingStatus.transcription_job.progress.total_chunks > 1 && (
                          <p className="text-xs text-slate-400">
                            Part {processingStatus.transcription_job.progress.current_chunk} of {processingStatus.transcription_job.progress.total_chunks}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {hasTranscript ? "Completed" :
                         meeting?.transcription_status === "processing" ? "In Progress..." :
                         "Pending"}
                      </p>
                    )}
                  </div>
                  {audioUpload && !hasTranscript && (
                    <div className="flex items-center gap-2">
                      {/* Show retry button if transcription failed */}
                      {processingStatus?.can_retry ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleRetryTranscription}
                          disabled={transcribing}
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        >
                          {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                          <span className="ml-1">Retry</span>
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleTranscribe}
                          disabled={transcribing || processingStatus?.is_processing}
                        >
                          {transcribing || processingStatus?.is_processing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {/* Show error if failed */}
                      {processingStatus?.transcription_job?.status === "failed" && !processingStatus?.is_processing && (
                        <span className="text-xs text-red-600" title={processingStatus?.transcription_job?.error}>
                          Failed - click Retry
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Connector */}
                <div className="ml-4 w-px h-4 bg-slate-200" />

                {/* Step 3: Minutes */}
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    hasMinutes ? "bg-emerald-100" : "bg-slate-100"
                  }`}>
                    {hasMinutes ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${hasMinutes ? "text-slate-900" : "text-slate-500"}`}>
                      Minutes Generated
                    </p>
                    {hasMinutes && minutes?.version && (
                      <p className="text-xs text-slate-500 mt-0.5">Version {minutes.version}</p>
                    )}
                  </div>
                  {hasTranscript && !hasMinutes && (
                    <Button
                      size="sm"
                      onClick={handleGenerateMinutes}
                      disabled={generating}
                      style={{ backgroundColor: primaryColor }}
                      className="text-white"
                    >
                      {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Exports Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="exports-card">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Exports</h2>
              
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleExport("docx")}
                  disabled={!hasMinutes || exporting}
                  data-testid="download-word-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download Word
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleExport("pdf")}
                  disabled={!hasMinutes || exporting}
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <div className="border-t border-slate-200 pt-3">
                  <Button
                    className="w-full text-white"
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => navigate(`/meetings/${meetingId}/minutes`)}
                    disabled={!hasMinutes}
                    data-testid="open-editor-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Open Editor
                  </Button>
                </div>
              </div>
            </div>

            {/* Uploads Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="uploads-card">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Uploads</h2>
              
              {/* Audio Section */}
              <div className="mb-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Audio Recording</p>
                {audioUpload ? (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Mic className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-slate-700 truncate max-w-[150px]">
                        {audioUpload.filename}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteUpload(audioUpload.id, audioUpload.filename)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    {...audioDropzone.getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                      audioDropzone.isDragActive ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    <input {...audioDropzone.getInputProps()} />
                    <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500">Drop audio file here</p>
                  </div>
                )}
              </div>

              {/* Documents Section */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Documents</p>
                {documents.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-slate-700 truncate max-w-[150px]">
                            {doc.filename}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUpload(doc.id, doc.filename)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  {...docDropzone.getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${
                    docDropzone.isDragActive ? "border-blue-400 bg-blue-50" : "border-slate-300 hover:border-slate-400"
                  }`}
                >
                  <input {...docDropzone.getInputProps()} />
                  <p className="text-sm text-slate-500">
                    <Plus className="w-4 h-4 inline mr-1" />
                    Add documents
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowResolutionModal(true)}
                >
                  <Gavel className="w-4 h-4 mr-2" />
                  Add Resolution
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowDeclarationModal(true)}
                >
                  <HandMetal className="w-4 h-4 mr-2" />
                  Add Declaration
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Status Summary */}
            <div className="hidden sm:flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5">
                {audioUpload ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Circle className="w-4 h-4 text-slate-300" />
                )}
                <span className={audioUpload ? "text-slate-700" : "text-slate-400"}>Audio</span>
              </span>
              <span className="flex items-center gap-1.5">
                {hasTranscript ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Circle className="w-4 h-4 text-slate-300" />
                )}
                <span className={hasTranscript ? "text-slate-700" : "text-slate-400"}>Transcription</span>
              </span>
              <span className="flex items-center gap-1.5">
                {hasMinutes ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Circle className="w-4 h-4 text-slate-300" />
                )}
                <span className={hasMinutes ? "text-slate-700" : "text-slate-400"}>Minutes</span>
              </span>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3 ml-auto">
              <Button
                variant="outline"
                onClick={() => handleExport("docx")}
                disabled={!hasMinutes || exporting}
              >
                <Download className="w-4 h-4 mr-2" />
                Word
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport("pdf")}
                disabled={!hasMinutes || exporting}
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button
                onClick={() => navigate(`/meetings/${meetingId}/minutes`)}
                disabled={!hasMinutes}
                className="text-white"
                style={{ backgroundColor: primaryColor }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Draft
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Add padding at bottom for sticky bar */}
      <div className="h-20" />

      {/* MODALS */}

      {/* Add Attendee Modal */}
      <Dialog open={showAttendeeModal} onOpenChange={setShowAttendeeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Attendee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newAttendee.name}
                onChange={(e) => setNewAttendee({ ...newAttendee, name: e.target.value })}
                placeholder="Full name"
                className="mt-1"
                data-testid="attendee-name-input"
              />
            </div>
            <div>
              <Label>Role/Title</Label>
              <Input
                value={newAttendee.role}
                onChange={(e) => setNewAttendee({ ...newAttendee, role: e.target.value })}
                placeholder="e.g., Director, Secretary"
                className="mt-1"
                data-testid="attendee-role-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newAttendee.email}
                onChange={(e) => setNewAttendee({ ...newAttendee, email: e.target.value })}
                placeholder="email@example.com"
                className="mt-1"
                data-testid="attendee-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Attendance Status</Label>
                <Select
                  value={newAttendee.status}
                  onValueChange={(value) => setNewAttendee({ ...newAttendee, status: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="attendee-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="apology">Apology</SelectItem>
                    <SelectItem value="invitee">Invitee/Observer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Director Type <span className="text-xs text-stone-400">(for quorum)</span></Label>
                <Select
                  value={newAttendee.director_type || "none"}
                  onValueChange={(value) => setNewAttendee({ ...newAttendee, director_type: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="mt-1" data-testid="attendee-director-type-select">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Applicable</SelectItem>
                    <SelectItem value="independent">Independent Director</SelectItem>
                    <SelectItem value="executive">Executive Director</SelectItem>
                    <SelectItem value="non_executive">Non-Executive Director</SelectItem>
                    <SelectItem value="alternate">Alternate Director</SelectItem>
                    <SelectItem value="observer">Observer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAttendeeModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddAttendee}
                disabled={!newAttendee.name.trim()}
                style={{ backgroundColor: primaryColor }}
                className="text-white"
                data-testid="add-attendee-submit-btn"
              >
                Add Attendee
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Resolution Modal */}
      <Dialog open={showResolutionModal} onOpenChange={setShowResolutionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5" style={{ color: primaryColor }} />
              Add Resolution
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Resolution Title *</Label>
              <Input
                value={newResolution.title}
                onChange={(e) => setNewResolution({ ...newResolution, title: e.target.value })}
                placeholder="e.g., Approval of Annual Budget"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Resolution Text</Label>
              <Textarea
                value={newResolution.text}
                onChange={(e) => setNewResolution({ ...newResolution, text: e.target.value })}
                placeholder="Full text of the resolution..."
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Moved By</Label>
                <Input
                  value={newResolution.moved_by}
                  onChange={(e) => setNewResolution({ ...newResolution, moved_by: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Seconded By</Label>
                <Input
                  value={newResolution.seconded_by}
                  onChange={(e) => setNewResolution({ ...newResolution, seconded_by: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vote Method</Label>
                <Select
                  value={newResolution.vote_method}
                  onValueChange={(value) => setNewResolution({ ...newResolution, vote_method: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="voice">Voice Vote</SelectItem>
                    <SelectItem value="show_of_hands">Show of Hands</SelectItem>
                    <SelectItem value="poll">Poll/Ballot</SelectItem>
                    <SelectItem value="unanimous_consent">Unanimous Consent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Outcome</Label>
                <Select
                  value={newResolution.outcome}
                  onValueChange={(value) => setNewResolution({ ...newResolution, outcome: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="tabled">Tabled</SelectItem>
                    <SelectItem value="withdrawn">Withdrawn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowResolutionModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddResolution}
                disabled={!newResolution.title.trim()}
                style={{ backgroundColor: primaryColor }}
                className="text-white"
              >
                Add Resolution
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Declaration Modal */}
      <Dialog open={showDeclarationModal} onOpenChange={setShowDeclarationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandMetal className="w-5 h-5 text-amber-600" />
              Add Declaration of Interest
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Person Name *</Label>
                <Input
                  value={newDeclaration.person_name}
                  onChange={(e) => setNewDeclaration({ ...newDeclaration, person_name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Role/Title</Label>
                <Input
                  value={newDeclaration.role_title}
                  onChange={(e) => setNewDeclaration({ ...newDeclaration, role_title: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Declaration Type</Label>
              <Select
                value={newDeclaration.declaration_type}
                onValueChange={(value) => setNewDeclaration({ ...newDeclaration, declaration_type: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="interest">Interest</SelectItem>
                  <SelectItem value="conflict">Conflict of Interest</SelectItem>
                  <SelectItem value="related_party">Related Party</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={newDeclaration.description}
                onChange={(e) => setNewDeclaration({ ...newDeclaration, description: e.target.value })}
                placeholder="Describe the nature of the interest..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Action Taken</Label>
              <Input
                value={newDeclaration.action_taken}
                onChange={(e) => setNewDeclaration({ ...newDeclaration, action_taken: e.target.value })}
                placeholder="e.g., Recused from discussion"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeclarationModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddDeclaration}
                disabled={!newDeclaration.person_name.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Add Declaration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Attendees Modal */}
      <Dialog open={showBulkImportModal} onOpenChange={setShowBulkImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" style={{ color: primaryColor }} />
              Import Attendees from CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2">CSV Format</p>
              <p className="text-xs text-slate-500 font-mono">
                name,email,role,status<br/>
                John Smith,john@example.com,Director,present<br/>
                Jane Doe,jane@example.com,Secretary,present
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Status options: present, apology, invitee
              </p>
            </div>
            
            <div>
              <Label>Upload CSV File</Label>
              <Input
                type="file"
                accept=".csv,.txt"
                onChange={handleCSVFileUpload}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Or Paste CSV Data</Label>
              <Textarea
                value={csvText}
                onChange={(e) => handleCSVChange(e.target.value)}
                placeholder="name,email,role,status&#10;John Smith,john@example.com,Director,present"
                className="mt-1 min-h-[120px] font-mono text-sm"
              />
            </div>
            
            {/* Preview */}
            {parsedAttendees.length > 0 && (
              <div>
                <Label className="text-emerald-600">Preview ({parsedAttendees.length} attendees)</Label>
                <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                  {parsedAttendees.map((att, idx) => (
                    <div key={idx} className="px-3 py-2 flex items-center justify-between text-sm">
                      <div>
                        <span className="font-medium text-slate-900">{att.name}</span>
                        {att.role && <span className="text-slate-500 ml-2">({att.role})</span>}
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">
                        {att.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => {
                setShowBulkImportModal(false);
                setCsvText("");
                setParsedAttendees([]);
              }}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkImport}
                disabled={parsedAttendees.length === 0 || importingAttendees}
                className="text-white"
                style={{ backgroundColor: primaryColor }}
              >
                {importingAttendees && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Import {parsedAttendees.length} Attendees
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quorum Rules Configuration Modal */}
      <Dialog open={showQuorumRulesModal} onOpenChange={setShowQuorumRulesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" style={{ color: primaryColor }} />
              Configure Quorum Rules
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <strong>Flexible Quorum Rules:</strong> Define minimum attendance requirements by total count and/or director type.
            </div>
            
            {/* Minimum Total Attendees */}
            <div>
              <Label>Minimum Total Attendees</Label>
              <Input
                type="number"
                min="0"
                value={quorumRule.min_total_attendees || ""}
                onChange={(e) => setQuorumRule({
                  ...quorumRule,
                  min_total_attendees: e.target.value ? parseInt(e.target.value) : null
                })}
                placeholder="e.g., 3"
                className="mt-1"
                data-testid="quorum-min-total"
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave empty if no minimum total is required
              </p>
            </div>
            
            {/* Type-based Requirements */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Director Type Requirements</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addQuorumTypeRequirement}
                  data-testid="add-type-requirement-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Rule
                </Button>
              </div>
              
              {quorumRule.type_requirements.length === 0 ? (
                <p className="text-sm text-slate-500 italic p-3 bg-slate-50 rounded">
                  No type-based requirements. Click &ldquo;Add Rule&rdquo; to require specific director types.
                </p>
              ) : (
                <div className="space-y-3">
                  {quorumRule.type_requirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <Select
                          value={req.director_type || ""}
                          onValueChange={(value) => updateQuorumTypeRequirement(index, "director_type", value)}
                        >
                          <SelectTrigger data-testid={`type-req-type-${index}`}>
                            <SelectValue placeholder="Select director type..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="independent">Independent Director</SelectItem>
                            <SelectItem value="executive">Executive Director</SelectItem>
                            <SelectItem value="non_executive">Non-Executive Director</SelectItem>
                            <SelectItem value="alternate">Alternate Director</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-24">
                        <Input
                          type="number"
                          min="1"
                          value={req.min_count}
                          onChange={(e) => updateQuorumTypeRequirement(index, "min_count", parseInt(e.target.value) || 1)}
                          placeholder="Min"
                          data-testid={`type-req-count-${index}`}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuorumTypeRequirement(index)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`remove-type-req-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Example */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>Example:</strong> &ldquo;2 independent directors + 1 executive director&rdquo; would be:
              <br />• Min Total: 3
              <br />• Rule 1: Independent → 2
              <br />• Rule 2: Executive → 1
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowQuorumRulesModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveQuorumRule}
                disabled={savingQuorumRule}
                style={{ backgroundColor: primaryColor }}
                className="text-white"
                data-testid="save-quorum-rule-btn"
              >
                {savingQuorumRule ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Save Quorum Rules
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
